import { Controller } from '@hotwired/stimulus'
import { stimulus } from '~/init'

// Opens a full-screen lightbox for any image with zoom + pan support.
//
// Usage on a trigger element:
//   data-controller="image-lightbox"
//   data-action="click->image-lightbox#open"
//   data-image-lightbox-src-value="<url>"
//   data-image-lightbox-alt-value="<alt>"
export default class ImageLightboxController extends Controller {
  static values = {
    src: String,
    alt: { type: String, default: '' }
  }

  connect () {
    this.scale = 1
    this.originX = 0
    this.originY = 0
    this.isDragging = false
    this.didDrag = false
  }

  open () {
    if (!this.srcValue) return
    this.#buildDialog()
    this.dialog.showModal()
  }

  // ── Private ────────────────────────────────────────────────────────────────

  #buildDialog () {
    // Reuse if already in DOM (e.g. opened twice)
    this.dialog?.remove()

    this.scale = 1
    this.originX = 0
    this.originY = 0

    this.dialog = document.createElement('dialog')
    this.dialog.className = 'lightbox-dialog'

    // Toolbar
    const toolbar = document.createElement('div')
    toolbar.className = 'lightbox-toolbar'
    toolbar.innerHTML = `
      <div class="lightbox-controls">
        <button type="button" class="lightbox-btn" data-action="zoom-out" title="Zoom out">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
        </button>
        <span class="lightbox-zoom-label">100%</span>
        <button type="button" class="lightbox-btn" data-action="zoom-in" title="Zoom in">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
        </button>
        <button type="button" class="lightbox-btn" data-action="reset" title="Reset zoom">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
        </button>
      </div>
      <button type="button" class="lightbox-btn lightbox-close" title="Close (Esc)">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    `

    // Image wrapper (handles pan)
    const wrapper = document.createElement('div')
    wrapper.className = 'lightbox-image-wrapper'

    this.img = document.createElement('img')
    this.img.src = this.srcValue
    this.img.alt = this.altValue
    this.img.className = 'lightbox-image'
    this.img.draggable = false

    wrapper.appendChild(this.img)
    this.dialog.appendChild(toolbar)
    this.dialog.appendChild(wrapper)
    document.body.appendChild(this.dialog)

    this.zoomLabel = toolbar.querySelector('.lightbox-zoom-label')

    // Events
    toolbar.querySelector('[data-action="zoom-in"]').addEventListener('click', () => this.#zoom(1.25))
    toolbar.querySelector('[data-action="zoom-out"]').addEventListener('click', () => this.#zoom(0.8))
    toolbar.querySelector('[data-action="reset"]').addEventListener('click', () => this.#reset())
    toolbar.querySelector('.lightbox-close').addEventListener('click', () => this.#close())

    this.dialog.addEventListener('click', e => {
      if (this.didDrag) { this.didDrag = false; return }
      if (e.target === this.dialog || e.target === wrapper) this.#close()
    })
    this.dialog.addEventListener('cancel', () => this.#close())

    wrapper.addEventListener('wheel', e => { e.preventDefault(); this.#zoom(e.deltaY < 0 ? 1.15 : 0.87) }, { passive: false })

    // Drag to pan
    wrapper.addEventListener('mousedown', e => this.#startDrag(e))
    window.addEventListener('mousemove', this._onMouseMove = e => this.#drag(e))
    window.addEventListener('mouseup', this._onMouseUp = () => { this.isDragging = false; wrapper.style.cursor = this.scale > 1 ? 'grab' : 'default' })
  }

  #zoom (factor) {
    this.scale = Math.min(Math.max(this.scale * factor, 0.5), 8)
    this.#applyTransform()
    this.zoomLabel.textContent = `${Math.round(this.scale * 100)}%`
  }

  #reset () {
    this.scale = 1
    this.originX = 0
    this.originY = 0
    this.#applyTransform()
    this.zoomLabel.textContent = '100%'
  }

  #applyTransform () {
    this.img.style.transform = `scale(${this.scale}) translate(${this.originX / this.scale}px, ${this.originY / this.scale}px)`
    const wrapper = this.img.parentElement
    wrapper.style.cursor = this.scale > 1 ? 'grab' : 'default'
  }

  #startDrag (e) {
    if (this.scale <= 1) return
    this.isDragging = true
    this.didDrag = false
    this.lastX = e.clientX
    this.lastY = e.clientY
    this.img.parentElement.style.cursor = 'grabbing'
  }

  #drag (e) {
    if (!this.isDragging) return
    this.didDrag = true
    this.originX += e.clientX - this.lastX
    this.originY += e.clientY - this.lastY
    this.lastX = e.clientX
    this.lastY = e.clientY
    this.#applyTransform()
  }

  disconnect () {
    this.#teardown()
  }

  #close () {
    this.#teardown()
  }

  #teardown () {
    window.removeEventListener('mousemove', this._onMouseMove)
    window.removeEventListener('mouseup', this._onMouseUp)
    this.dialog?.close()
    this.dialog?.remove()
    this.dialog = null
    this.scale = 1
    this.originX = 0
    this.originY = 0
    this.isDragging = false
    this.didDrag = false
  }
}

stimulus.register('image-lightbox', ImageLightboxController)
